import { useState, useCallback, useMemo, useRef } from "react";
import {
  Stack,
  Group,
  Button,
  Box,
  Text,
  SimpleGrid,
  Divider,
  Modal,
} from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconAlertCircle } from "@tabler/icons-react";
import {
  CertaintySelect,
  CertaintyTextInput,
  CertaintyRichText,
} from "./CertaintyInput";
import GrossTemplateSelector, {
  type GrossCodeOption,
  type GrossTemplateSelectorHandle,
} from "../grossing/GrossTemplateSelector";
import CollectionMethodForm, {
  type CollectionMethodFormValues,
} from "../admin/collection-methods/CollectionMethodForm";
import { useCreateCollectionMethod } from "../../hooks/admin/useCollectionMethods";
import { showValidationNotification } from "../../utils/formValidation";
import type { FragmentCountPatch } from "../../utils/fragmentCount";
import {
  MATERIAL_OPTIONS,
  CONSULTATION_MATERIAL_VALUES,
  GROSS_DESCRIPTION_MATERIALS,
  getMaterialLabel,
} from "./specimenConstants";

const sanitizeOptions = <T extends { value: string | null | undefined }>(
  opts: T[] | undefined
): T[] => {
  if (!Array.isArray(opts)) return [];
  return opts.filter(
    (o) => typeof o.value !== "undefined" && o.value !== null && o.value !== ""
  );
};

/** Specimen fields whose change should bump certainty to 100 on save (mirrors inline edits). */
const CERTAINTY_FIELDS = [
  "sourceId",
  "subsiteId",
  "findings",
  "collectionMethod",
  "material",
  "comments",
  "gross",
  "grossCodeId",
];

interface SourceOption {
  value: string;
  label: string;
  defaultCollectionMethodId?: string;
  defaultCollectionMethodName?: string;
}

interface SpecimenEditFormProps {
  index: number;
  specimen: any;
  form: UseFormReturnType<any>;
  onCertaintyChange?: (field: string, certainty: number) => void;
  sources: SourceOption[];
  subsites: Record<string, Array<{ value: string; label: string }>>;
  collectionMethods: Array<{ value: string; label: string }>;
  grossCodes: GrossCodeOption[];
  isConsultation?: boolean;
  assignedPrefix?: string;
  getSourceName: (sourceId: string | null | undefined) => string | undefined;
  getSubsiteName: (
    sourceId: string | null | undefined,
    subsiteId: string | null | undefined
  ) => string | undefined;
  onClose: () => void;
}

export default function SpecimenEditForm({
  index,
  specimen,
  form,
  onCertaintyChange,
  sources,
  subsites,
  collectionMethods,
  grossCodes,
  isConsultation,
  assignedPrefix,
  getSourceName,
  getSubsiteName,
  onClose,
}: SpecimenEditFormProps) {
  // Local draft — edits apply to the form only when the user clicks Save.
  const [draft, setDraft] = useState<any>(() => ({ ...specimen }));
  const [sourceError, setSourceError] = useState<string | null>(null);
  const grossRef = useRef<GrossTemplateSelectorHandle>(null);

  // Quick-add collection method — writes the new method into the DRAFT so it
  // survives Save (the form is not touched until the user saves the specimen).
  const createCollectionMethod = useCreateCollectionMethod();
  const [
    collectionMethodModalOpened,
    { open: openCollectionMethodModal, close: closeCollectionMethodModal },
  ] = useDisclosure(false);

  const handleCollectionMethodSubmit = useCallback(
    async (values: CollectionMethodFormValues) => {
      const method = await createCollectionMethod.mutateAsync({
        name: values.name,
        description: values.description || undefined,
        isActive: values.isActive,
      });
      setDraft((d: any) => ({ ...d, collectionMethod: method.name }));
      closeCollectionMethodModal();
    },
    [createCollectionMethod, closeCollectionMethodModal]
  );

  const materialOptions = useMemo(
    () =>
      isConsultation
        ? MATERIAL_OPTIONS.filter((o) =>
            CONSULTATION_MATERIAL_VALUES.has(o.value)
          )
        : MATERIAL_OPTIONS,
    [isConsultation]
  );

  const availableSubsites = useMemo(
    () => subsites[draft.sourceId] || [],
    [subsites, draft.sourceId]
  );

  const filteredGrossCodes = useMemo(() => {
    // 1. Exclude retired AS_ templates
    const activeCodes = grossCodes.filter((c) => !c.label.startsWith("AS_"));

    if (!assignedPrefix) return activeCodes;

    const prefix = assignedPrefix.toUpperCase();
    let filtered: GrossCodeOption[] = [];
    const isGeneric = (code: string) => !code.includes("_");

    // Filtering rules based on CASE-0004 Decision Context
    if (
      prefix.startsWith("G") &&
      !["GC", "GM", "GP"].some((p) => prefix.startsWith(p))
    ) {
      // General G cases can use every G-family accessioning template.
      filtered = activeCodes.filter((c) => {
        const code = c.label.toUpperCase();
        return isGeneric(code) || code.startsWith("G");
      });
    } else if (prefix === "GC") {
      // GC cases match GC_ and generic
      filtered = activeCodes.filter((c) => {
        const code = c.label.toUpperCase();
        return isGeneric(code) || code.startsWith("GC_");
      });
    } else if (prefix === "GM") {
      // GM cases match GM_ and generic
      filtered = activeCodes.filter((c) => {
        const code = c.label.toUpperCase();
        return isGeneric(code) || code.startsWith("GM_");
      });
    } else if (prefix.startsWith("GP")) {
      // GP cases match GP_, GP1_, GP2_, GP3_, GPM_, GPS_ and generic
      filtered = activeCodes.filter((c) => {
        const code = c.label.toUpperCase();
        return (
          isGeneric(code) ||
          code.startsWith("GP_") ||
          /^GP\d+_/.test(code) ||
          code.startsWith("GPM_") ||
          code.startsWith("GPS_")
        );
      });
    } else if (prefix === "HG") {
      // HG cases match HG_ and generic
      filtered = activeCodes.filter((c) => {
        const code = c.label.toUpperCase();
        return isGeneric(code) || code.startsWith("HG_");
      });
    }

    // Fallback: If no match or other prefix, show all active non-AS templates
    if (filtered.length === 0) {
      return activeCodes;
    }

    return filtered;
  }, [grossCodes, assignedPrefix]);

  const showGross = useMemo(
    () => GROSS_DESCRIPTION_MATERIALS.has(draft.material),
    [draft.material]
  );

  const hasExternalData = useMemo(
    () =>
      Boolean(
        draft.extractedStainCodes ||
          draft.extractedGrossCodePrefix ||
          draft.extractedBlockIdentifiers ||
          draft.extractedDimensions
      ),
    [
      draft.extractedStainCodes,
      draft.extractedGrossCodePrefix,
      draft.extractedBlockIdentifiers,
      draft.extractedDimensions,
    ]
  );

  const handleSourceChange = useCallback(
    (value: string | null) => {
      if (value) setSourceError(null);
      setDraft((d: any) => {
        const next = { ...d, sourceId: value, subsiteId: null };
        const selectedSource = sources.find((s) => s.value === value);
        if (selectedSource?.defaultCollectionMethodName) {
          next.collectionMethod = selectedSource.defaultCollectionMethodName;
        }
        return next;
      });
    },
    [sources]
  );

  const handleSubsiteChange = useCallback((value: string | null) => {
    setDraft((d: any) => ({ ...d, subsiteId: value }));
  }, []);

  const handleFindingsChange = useCallback((value: string) => {
    setDraft((d: any) => ({ ...d, findings: value }));
  }, []);

  const handleMethodChange = useCallback((value: string | null) => {
    setDraft((d: any) => ({ ...d, collectionMethod: value }));
  }, []);

  const handleMaterialChange = useCallback((value: string | null) => {
    setDraft((d: any) => ({ ...d, material: value }));
  }, []);

  const handleCommentsChange = useCallback((value: string) => {
    setDraft((d: any) => ({ ...d, comments: value }));
  }, []);

  const handleGrossChange = useCallback((value: string) => {
    setDraft((d: any) => ({ ...d, gross: value }));
  }, []);

  const handleGrossCodeChange = useCallback((id: string | null) => {
    setDraft((d: any) => ({ ...d, grossCodeId: id }));
  }, []);

  const handleFragmentCountChange = useCallback((patch: FragmentCountPatch) => {
    setDraft((d: any) => ({
      ...d,
      fragmentCount: patch.fragmentCount,
      fragmentCountQualifier: patch.fragmentCountQualifier,
    }));
  }, []);

  const handleSave = useCallback(() => {
    if (!draft.sourceId) {
      setSourceError("Source is required");
      showValidationNotification("Please select a source before saving.");
      return;
    }
    const flushedGross = grossRef.current?.flushPendingContent?.();
    const finalDraft =
      flushedGross != null ? { ...draft, gross: flushedGross } : draft;
    form.setFieldValue(`case.specimens.${index}`, finalDraft);
    CERTAINTY_FIELDS.forEach((key) => {
      if (finalDraft[key] !== specimen[key]) {
        onCertaintyChange?.(`case.specimens.${index}.${key}`, 100);
      }
    });
    onClose();
  }, [draft, form, index, specimen, onCertaintyChange, onClose]);

  return (
    <>
      <Modal.Body style={{ flex: 1, overflow: "auto" }}>
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <CertaintySelect
              label="Source"
              placeholder="Source"
              required
              error={sourceError}
              data={sanitizeOptions(sources)}
              searchable
              value={draft.sourceId || null}
              onChange={handleSourceChange}
            />
            <CertaintySelect
              label="Subsite"
              placeholder="Subsite"
              data={sanitizeOptions(availableSubsites)}
              searchable
              clearable
              disabled={!draft.sourceId}
              value={draft.subsiteId || null}
              onChange={handleSubsiteChange}
            />
          </SimpleGrid>

          <CertaintyTextInput
            label="Findings from requisition"
            placeholder="Findings from requisition"
            value={draft.findings || ""}
            onChange={handleFindingsChange}
          />

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <Box>
              <CertaintySelect
                label="Collection Method"
                placeholder="Method"
                data={sanitizeOptions(collectionMethods)}
                searchable
                clearable
                value={draft.collectionMethod || null}
                onChange={handleMethodChange}
              />
              <Group justify="flex-end" mt={4}>
                <Button
                  variant="subtle"
                  size="compact-xs"
                  onClick={openCollectionMethodModal}
                >
                  Add New
                </Button>
              </Group>
            </Box>
            <CertaintySelect
              label="Material (received as)"
              placeholder="Material"
              data={materialOptions}
              searchable
              clearable
              value={draft.material || null}
              onChange={handleMaterialChange}
            />
          </SimpleGrid>

          <CertaintyRichText
            label="Comments"
            placeholder="Additional notes or observations"
            value={draft.comments || ""}
            onChange={handleCommentsChange}
          />

          {hasExternalData && (
            <SimpleGrid cols={{ base: 1, sm: 3 }}>
              <CertaintyTextInput
                label="Stain Codes"
                placeholder="e.g., ABP/HP, Tri/CD3"
                readOnly
                size="xs"
                value={draft.extractedStainCodes || ""}
              />
              <CertaintyTextInput
                label="Gross Code Prefix"
                placeholder="e.g., G2, GM"
                readOnly
                size="xs"
                value={draft.extractedGrossCodePrefix || ""}
              />
              <CertaintyTextInput
                label="Block IDs"
                placeholder="e.g., A1, A2"
                readOnly
                size="xs"
                value={draft.extractedBlockIdentifiers || ""}
              />
              <CertaintyTextInput
                label="Dimensions"
                placeholder="e.g., 0.4 x 0.3 x 0.1"
                readOnly
                size="xs"
                value={draft.extractedDimensions || ""}
              />
            </SimpleGrid>
          )}

          {showGross && (
            <>
              <Box
                p="xs"
                style={{
                  backgroundColor: "var(--mantine-color-blue-0)",
                  borderRadius: "var(--mantine-radius-sm)",
                  border: "1px solid var(--mantine-color-blue-2)",
                }}
              >
                <Group gap="xs">
                  <IconAlertCircle
                    size={16}
                    color="var(--mantine-color-blue-6)"
                  />
                  <Text size="sm" c="blue.7" fw={500}>
                    This specimen was received as a{" "}
                    {getMaterialLabel(draft.material).toLowerCase()}. Please
                    review and complete the gross description below.
                  </Text>
                </Group>
              </Box>
              <GrossTemplateSelector
                ref={grossRef}
                value={draft.gross || ""}
                onChange={handleGrossChange}
                grossCodeId={draft.grossCodeId}
                onGrossCodeChange={handleGrossCodeChange}
                grossCodes={filteredGrossCodes}
                onFragmentCountChange={handleFragmentCountChange}
                fragmentCount={draft.fragmentCount ?? null}
                fragmentCountQualifier={draft.fragmentCountQualifier ?? null}
                specimenLetter={draft.number}
                specimenCount={index + 1}
                sourceName={getSourceName(draft.sourceId)}
                subsiteName={getSubsiteName(draft.sourceId, draft.subsiteId)}
                collectionMethod={draft.collectionMethod}
                sizeCm={draft.extractedDimensions}
                cassetteCount={draft.blockCount || 1}
                blockCount={draft.blockCount || 1}
              />
            </>
          )}
        </Stack>
      </Modal.Body>

      <Divider />
      <Group justify="space-between" p="md">
        <Button variant="default" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSave}>Save</Button>
      </Group>

      {/* Quick-Add Collection Method Modal — updates the draft, not the form */}
      <Modal
        opened={collectionMethodModalOpened}
        onClose={closeCollectionMethodModal}
        title="Add New Collection Method"
        size="md"
        closeOnClickOutside={false}
      >
        <CollectionMethodForm
          onSubmit={handleCollectionMethodSubmit}
          onCancel={closeCollectionMethodModal}
          isSubmitting={createCollectionMethod.isPending}
          submitLabel="Create"
        />
      </Modal>
    </>
  );
}
